import styles from './TeamMemberActionDialogs.module.scss'

import { AddBillingInfoButton } from '@/components/AddBillingInfoButton'
import Button from '@/components/Button'
import DialogWrapper from '@/components/Dialog'
import { TeamMemberActionDialog } from '@/hooks/useTeamMemberActions'
import { PaymentSchedule } from '@/types'

interface TeamMemberActionDialogsProps {
  activeDialog: TeamMemberActionDialog
  onClose: () => void
  onExecute: (skipEligibilityCheck?: boolean) => void
  defaultSchedule?: PaymentSchedule
}

/**
 * Reusable component that renders all team member action dialogs.
 * Works in conjunction with useTeamMemberActions hook.
 *
 * Usage:
 * ```tsx
 * const { activeDialog, closeDialog, executeAction } = useTeamMemberActions()
 *
 * return (
 *   <>
 *     <TeamMemberActionDialogs
 *       activeDialog={activeDialog}
 *       onClose={closeDialog}
 *       onExecute={executeAction}
 *     />
 *     // ... rest of your component
 *   </>
 * )
 * ```
 */
export const TeamMemberActionDialogs = ({
  activeDialog,
  onClose,
  onExecute,
  defaultSchedule,
}: TeamMemberActionDialogsProps) => {
  const handleUseBillingInfoClick = () => {
    onClose()
    onExecute(true)
  }

  const handleInviteWithoutTrial = () => {
    onExecute(true)
  }

  return (
    <>
      {/* Dialog: User will become payer */}
      <DialogWrapper
        isOpen={activeDialog?.type === 'willBecomePayer'}
        title="Do you want to pay for your team?"
        subtitle={`Your team does not have billing information. You can either add billing information to your team or ${activeDialog?.email || 'this user'}'s will be used when they join the team.`}
        close={onClose}
      >
        <div className={styles.dialogFooter}>
          <Button
            title={`Use ${activeDialog?.email || 'this user'}'s billing info`}
            onClick={handleUseBillingInfoClick}
            type="light"
          />
          <AddBillingInfoButton title="Add billing info" type="dark" defaultSchedule={defaultSchedule} />
        </div>
      </DialogWrapper>

      {/* Dialog: Team needs billing info */}
      <DialogWrapper
        isOpen={activeDialog?.type === 'teamNeedsBillingInfo'}
        title="No trial available for this user"
        subtitle="Your team does not have billing information. You must add billing information to add this user because they do not have a trial available."
        close={onClose}
      >
        <div className={styles.dialogFooter}>
          <AddBillingInfoButton title="Add billing info" type="dark" defaultSchedule={defaultSchedule} />
        </div>
      </DialogWrapper>

      {/* Dialog: User already used trial */}
      <DialogWrapper
        isOpen={activeDialog?.type === 'warnEnterprise'}
        title="This user does not have a trial available"
        subtitle="This user has already used their available trial. They will join the team on a paid seat when you add them."
        close={onClose}
      >
        <div className={styles.dialogFooter}>
          <Button title="Not Now" onClick={onClose} type="light" />
          <Button title="Add user" onClick={handleInviteWithoutTrial} type="dark" />
        </div>
      </DialogWrapper>
    </>
  )
}
